/**
 * Lista zapisanych ustawień z API (GET sales-crm/filters), wybór presetu,
 * zastosowanie presetu (filtry + widoczność + kolejność) oraz wyczyść zapisane ustawienia.
 */
import { useMemo, useCallback } from 'react';
import { useDispatch } from 'store';
import { reduxActions } from 'store';
import { useGetSalesCrmFilters } from 'api';
import type { IFilterRow } from 'components/wise/types';
import { getEmptyFilterRow } from 'components/wise/WiseTable/constants';
import { filtersToGroups } from 'components/wise/WiseTable/utils/filterGroups';
import type { ISavedSettingsOptionItem } from './types';
import type { ISalesCrmFilterPreset } from 'api/endpoints/useGetSalesCrmFilters';

export interface UseSavedSettingsPresetsParams {
  componentSymbol: string;
  advancedFiltersDialogOpen: boolean;
  isDynamicUI: boolean;
  pageSymbol?: string;
  setFilterRuleGroups: React.Dispatch<React.SetStateAction<IFilterRow[][]>>;
  setPendingColumnVisibility: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  setPendingColumnOrderState: React.Dispatch<React.SetStateAction<string[]>>;
  /** Ustawia lokalny wybór presetu w dialogu (presetId trafia do Redux dopiero po „Zastosuj”). */
  setPendingSelectedPresetId: (id: string | null) => void;
}

export function useSavedSettingsPresets({
  componentSymbol,
  advancedFiltersDialogOpen,
  isDynamicUI,
  pageSymbol,
  setFilterRuleGroups,
  setPendingColumnVisibility,
  setPendingColumnOrderState,
  setPendingSelectedPresetId
}: UseSavedSettingsPresetsParams) {
  const dispatch = useDispatch();
  const { data: apiFiltersData } = useGetSalesCrmFilters(componentSymbol, {
    enabled: advancedFiltersDialogOpen
  });
  const apiFilterItems: ISalesCrmFilterPreset[] = apiFiltersData?.items ?? [];

  const savedSettingsOptionsForSelect = useMemo(
    () =>
      apiFilterItems.map((i) => ({
        value: String(i.id),
        label: i.name,
        item: {
          filters: i.filters,
          column_visibility: i.column_visibility,
          optionValue: String(i.id)
        } as ISavedSettingsOptionItem
      })),
    [apiFilterItems]
  );

  const handleSelectSettingsPreset = useCallback(
    (item: ISavedSettingsOptionItem | null) => {
      const presetId = item?.optionValue ?? null;
      setPendingSelectedPresetId(presetId);
    },
    [setPendingSelectedPresetId]
  );

  const handleClearSavedFilters = useCallback(() => {
    setFilterRuleGroups([[getEmptyFilterRow()]]);
    setPendingColumnVisibility({});
    setPendingColumnOrderState([]);
    if (isDynamicUI && pageSymbol) {
      dispatch(
        reduxActions.setWiseTableAdvancedFilters({
          pageSymbol,
          componentSymbol,
          filters: []
        })
      );
      dispatch(
        reduxActions.setWiseTableColumnVisibility({
          pageSymbol,
          componentSymbol,
          columnVisibility: {}
        })
      );
      dispatch(
        reduxActions.setWiseTableColumnOrder({
          pageSymbol,
          componentSymbol,
          columnOrder: []
        })
      );
      dispatch(
        reduxActions.setWiseTableSelectedSettingsPresetId({
          pageSymbol,
          componentSymbol,
          presetId: null
        })
      );
    } else {
      dispatch(
        reduxActions.setTraderPanelWiseTableAdvancedFilters({
          componentSymbol,
          filters: []
        })
      );
      dispatch(
        reduxActions.setTraderPanelWiseTableColumnVisibility({
          componentSymbol,
          columnVisibility: {}
        })
      );
      dispatch(
        reduxActions.setTraderPanelWiseTableColumnOrder({
          componentSymbol,
          columnOrder: []
        })
      );
      dispatch(
        reduxActions.setTraderPanelWiseTableSelectedSettingsPresetId({
          componentSymbol,
          presetId: null
        })
      );
    }
    setPendingSelectedPresetId(null);
  }, [
    isDynamicUI,
    pageSymbol,
    componentSymbol,
    dispatch,
    setFilterRuleGroups,
    setPendingColumnVisibility,
    setPendingColumnOrderState,
    setPendingSelectedPresetId
  ]);

  const handleApplySettingsPreset = useCallback(
    (selectedSettingsPresetName: string | null) => {
      if (selectedSettingsPresetName == null) return;
      const preset = apiFilterItems.find((i) => String(i.id) === selectedSettingsPresetName);
      if (!preset) return;

      if (isDynamicUI && pageSymbol) {
        dispatch(
          reduxActions.setWiseTableSelectedSettingsPresetId({
            pageSymbol,
            componentSymbol,
            presetId: selectedSettingsPresetName
          })
        );
      } else {
        dispatch(
          reduxActions.setTraderPanelWiseTableSelectedSettingsPresetId({
            componentSymbol,
            presetId: selectedSettingsPresetName
          })
        );
      }

      if (preset.filters?.length) {
        if (isDynamicUI && pageSymbol) {
          dispatch(
            reduxActions.setWiseTableAdvancedFilters({
              pageSymbol,
              componentSymbol,
              filters: preset.filters
            })
          );
        } else {
          dispatch(
            reduxActions.setTraderPanelWiseTableAdvancedFilters({
              componentSymbol,
              filters: preset.filters
            })
          );
        }
        setFilterRuleGroups(
          filtersToGroups(preset.filters).length > 0
            ? filtersToGroups(preset.filters)
            : [[getEmptyFilterRow()]]
        );
      }
      if (preset.column_visibility && Object.keys(preset.column_visibility).length > 0) {
        const visibility = preset.column_visibility;
        setPendingColumnVisibility(visibility);
        if (isDynamicUI && pageSymbol) {
          dispatch(
            reduxActions.setWiseTableColumnVisibility({
              pageSymbol,
              componentSymbol,
              columnVisibility: visibility
            })
          );
        } else {
          dispatch(
            reduxActions.setTraderPanelWiseTableColumnVisibility({
              componentSymbol,
              columnVisibility: visibility
            })
          );
        }
      }
      if (preset.column_order && preset.column_order.length > 0) {
        setPendingColumnOrderState(preset.column_order);
        if (isDynamicUI && pageSymbol) {
          dispatch(
            reduxActions.setWiseTableColumnOrder({
              pageSymbol,
              componentSymbol,
              columnOrder: preset.column_order
            })
          );
        } else {
          dispatch(
            reduxActions.setTraderPanelWiseTableColumnOrder({
              componentSymbol,
              columnOrder: preset.column_order
            })
          );
        }
      }
    },
    [
      apiFilterItems,
      isDynamicUI,
      pageSymbol,
      componentSymbol,
      dispatch,
      setFilterRuleGroups,
      setPendingColumnVisibility,
      setPendingColumnOrderState
    ]
  );

  return {
    apiFilterItems,
    savedSettingsOptionsForSelect,
    handleSelectSettingsPreset,
    handleApplySettingsPreset,
    handleClearSavedFilters
  };
}
